import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import {
  Form,
  InputText,
  useFormConfig,
  Button,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
  displayErrorToast,
  Link,
} from "@endya-conformite/ui-kit";
import { GatewayAuthApi } from "@src/api/auth.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import XIcon from "@endya-conformite/ui-kit/src/assets/icons/x-lg.svg?react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useState } from "react";
import { ForgotPasswordOutletContext } from "./ForgotPassword";
import style from "./ForgotPasswordMail.module.scss";

const schema = zod.object({
  email: zod.string().nonempty().email(),
});

export type ForgotPasswordMailFormValue = zod.infer<typeof schema>;

export function ForgotPasswordMail() {
  const navigate = useNavigate();
  const { setData, data } = useOutletContext<ForgotPasswordOutletContext>();
  const formConfig = useFormConfig<typeof schema, ForgotPasswordMailFormValue>({
    schema,
    defaultValues: { email: data.email },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (
    submittedData: ForgotPasswordMailFormValue
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await GatewayAuthApi.forgotPassword(submittedData.email);
      setData({ email: submittedData.email });
      navigate("/forgot-password/reset");
    } catch (err) {
      handleGatewayError({
        onResponse: (error) => {
          displayErrorToast(
            error.response.data.message ||
              "Une erreur est survenue lors de l'envoi du code. Veuillez réessayer."
          );
        },
        onUnhandled: () => {
          displayErrorToast(
            "Impossible de contacter le serveur. Veuillez vérifier votre connexion internet et réessayer."
          );
        },
      })(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form formConfig={formConfig} onSubmit={handleSubmit}>
      <FormGrid disableHorizontalGutter>
        <FormTitle>
          <h1>Mot de passe oublié</h1>
        </FormTitle>
        <FormRow>
          <FormField>
            <FormNotice>
              Renseignez votre adresse mail, nous vous enverrons un lien pour
              récupérer votre compte.
            </FormNotice>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputText<ForgotPasswordMailFormValue>
              name="email"
              label="Adresse email"
            />
          </FormField>
        </FormRow>

        <FormAction className={style.actions}>
          <Link to="/login">
            <Button type="button" variant="text" icon={<XIcon />}>
              Annuler
            </Button>
          </Link>
          <Button
            type="submit"
            disabled={isSubmitting}
            icon={isSubmitting ? <Loader /> : undefined}
          >
            Envoyer le code par email
          </Button>
        </FormAction>
      </FormGrid>
    </Form>
  );
}
